import { normalizeEuro } from '@amo/core/utils/normalize';
import { getTranslation } from '@amo/core/utils/index.js';
import { useState, useEffect, useRef } from 'react';
import _ from 'lodash';

const StickyPriceBar = (props) => {
    const [isStuck, setIsStuck] = useState(false);
    const [souscrireVisible, setSouscrireVisible] = useState(false);
    const sentinelRef = useRef(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const souscrireEl = document.getElementById('souscrire');
        if (!souscrireEl) return;

        const observer = new IntersectionObserver(
            ([entry]) => setSouscrireVisible(entry.isIntersecting),
            { threshold: 0.5 }
        );
        observer.observe(souscrireEl);
        return () => observer.disconnect();
    }, []);

    const {
        vehicule,
        formuleChoisie,
        nbOptions,
        prixTotalMensuelPromo,
        prixTotalAnnuelPromo,
        fractionnementChoisi,
        changeValue,
        data,
        codePromo,
        prixTotalMensuel,
        prixTotalAnnuel,
        analyticsSetEvent,
        postFill,
        options,
    } = props;

    const optionsChoisi = _.get(data, 'options');
    const optionsListe = _.get(data, 'optionsListe');
    const optionsFranchise = _.get(data, 'optionsFranchise');

    const selectedOptionNames = [];
    if (optionsChoisi) {
        _.forEach(optionsChoisi, (value, key) => {
            if (value) {
                const group = _.find(options, ['idGroupe', key]);
                if (group?.labelGroupe) selectedOptionNames.push(group.labelGroupe);
            }
        });
    }
    if (optionsListe) {
        _.forEach(optionsListe, (value, key) => {
            if (value) {
                const group = _.find(options, ['idGroupe', key]);
                if (group?.labelGroupe) selectedOptionNames.push(group.labelGroupe);
            }
        });
    }
    if (optionsFranchise) {
        _.forEach(optionsFranchise, (value, key) => {
            if (value === 'true') {
                const name = key === 'RAFRAVI' ? 'Rachat franchise vol/incendie' : key === 'RAFRADTA' ? 'Rachat franchise tous accidents' : key;
                selectedOptionNames.push(name);
            }
        });
    }

    const handleFractionnement = (value) => {
        analyticsSetEvent({
            event: 'mensuelAnnuel',
            datas: {
                mensuelAnnuel: {
                    bouton: value === 'A' ? 'Annuel' : 'Mensuel',
                },
            },
        });
        changeValue('DemandeTarif[Police][FractionnementChoisi]', value);
    };

    const handleSouscrire = () => {
        if (souscrireVisible) {
            changeValue('typeDevis', 'subscribe');
            postFill({ getTarif: false });
        } else {
            document.getElementById('souscrire')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const isMensuel = fractionnementChoisi === 'M';
    const prix = isMensuel ? prixTotalMensuelPromo : prixTotalAnnuelPromo;
    const prixSansPromo = isMensuel ? prixTotalMensuel : prixTotalAnnuel;
    const hasPromo = codePromo?.Eligible && prixSansPromo && prix < prixSansPromo;

    const prixFormule = isMensuel
        ? formuleChoisie?.PrimeMensuelleBase
        : formuleChoisie?.PrimeAnnuelleFracAnnuelBase;
    const prixOptions = prix != null && prixFormule != null ? Math.round((prix - prixFormule) * 100) / 100 : null;

    const fracToggle = (
        <div className="sticky-price-bar__frac-toggle">
            <button
                className={`sticky-price-bar__frac-btn ${isMensuel ? 'active' : ''}`}
                onClick={() => handleFractionnement('M')}
            >
                Mensuel
            </button>
            <button
                className={`sticky-price-bar__frac-btn ${!isMensuel ? 'active' : ''}`}
                onClick={() => handleFractionnement('A')}
            >
                Annuel <span className="sticky-price-bar__frac-badge">-10%</span>
            </button>
        </div>
    );

    const priceBlock = (
        <div className="sticky-price-bar__price-block">
            <div className="sticky-price-bar__price-main">
                {normalizeEuro(prix)}
            </div>
            {hasPromo && (
                <div className="sticky-price-bar__price-old">
                    {normalizeEuro(prixSansPromo)}
                </div>
            )}
            <div className="sticky-price-bar__price-sub">
                TTC / {isMensuel ? getTranslation('months') : getTranslation('year')}
            </div>
        </div>
    );

    return (
        <>
        <div ref={sentinelRef} style={{height: 0}} />
        <div className={`sticky-price-bar ${isStuck ? 'sticky-price-bar--scrolled' : ''}`}>
            <div className={`sticky-price-bar__left ${isStuck ? 'sticky-price-bar__left--hidden' : ''}`}>
                    <div className="sticky-price-bar__formule">
                        Formule {formuleChoisie?.LibelleFormule} ({normalizeEuro(prixFormule)})
                        {nbOptions >= 1 && (
                            <span className="sticky-price-bar__options">
                                {' '}+ {nbOptions} option{nbOptions > 1 ? 's' : ''} ({normalizeEuro(prixOptions)})
                            </span>
                        )}
                    </div>
                    {selectedOptionNames.length > 0 && (
                        <div className="sticky-price-bar__options-list">
                            {selectedOptionNames.join(', ')}
                        </div>
                    )}
                </div>

                {/* Mobile/PWA : résumé formule + options (visible uniquement au scroll) */}
                <div className={`sticky-price-bar__mobile-summary ${!isStuck ? 'sticky-price-bar__mobile-summary--hidden' : ''}`}>
                    Formule {formuleChoisie?.LibelleFormule}
                    {nbOptions >= 1 && ` + ${nbOptions} option${nbOptions > 1 ? 's' : ''}`}
                </div>

                {/* Mobile : prix + toggle wrappés ensemble */}
                <div className="sticky-price-bar__row-mobile">
                    {priceBlock}
                    {fracToggle}
                </div>

                {/* Desktop : prix et toggle séparés dans le flex parent */}
                <div className="sticky-price-bar__desktop-frac">
                    {fracToggle}
                </div>
                <div className="sticky-price-bar__desktop-price">
                    {priceBlock}
                </div>

                <button
                    className="sticky-price-bar__btn-souscrire"
                    onClick={handleSouscrire}
                    aria-label="Aller à la section souscription"
                >
                    {getTranslation('subscribe')}
                </button>
        </div>
        </>
    );
};

export default StickyPriceBar;
